import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Search,
  Pencil,
  Archive,
  ArrowUpRight,
  Users,
  Monitor,
  Ticket,
  ShieldCheck,
} from "lucide-react";
import { Ring } from "@/components/Primitives";

const EMPTY_FORM = {
  name: "",
  legal_name: "",
  industry: "",
  country: "Philippines",
  region: "",
  timezone: "Asia/Manila",
  currency: "PHP",
  employee_count: 0,
  support_tier: "Silver",
  lifecycle_stage: "active",
  contract_status: "active",
  primary_contact_name: "",
  primary_contact_email: "",
  primary_contact_phone: "",
  assigned_engineer: "",
  assigned_account_manager: "",
  assigned_vcio: "",
  notes: "",
  health_score: 85,
  compliance_score: 0,
  security_score: 0,
};

export default function Clients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [stage, setStage] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", {
        params: {
          q: q || undefined,
          status,
          lifecycle_stage: stage,
        },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status, stage]);

  const totals = useMemo(() => {
    return {
      clients: items.length,
      assets: items.reduce((sum, c) => sum + Number(c.asset_count || c.assets_count || 0), 0),
      users: items.reduce((sum, c) => sum + Number(c.user_count || c.users_count || 0), 0),
      tickets: items.reduce((sum, c) => sum + Number(c.open_ticket_count || c.tickets_open || 0), 0),
    };
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({ ...EMPTY_FORM, ...client });
    setModalOpen(true);
  };

  const saveClient = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Company name is required");
      return;
    }

    setSaving(true);

    try {
      if (editing) {
        await api.patch(`/clients/${editing.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client created");
      }

      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const archiveClient = async (client) => {
    const ok = window.confirm(
      `Archive ${client.name}? This will not hard-delete historical records, tickets, agents, assets, or audit logs.`
    );

    if (!ok) return;

    try {
      await api.delete(`/clients/${client.id}`);
      toast.success("Client archived");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
            Client Management
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">
            Companies
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Add, manage, archive, and drill into client companies. This is now the anchor object for MSPOS.
          </p>
        </div>

        <button
          onClick={openCreate}
          data-testid="create-client-btn"
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm px-4 py-2 rounded-md"
        >
          <Plus size={15} />
          Add Company
        </button>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Metric label="Companies" value={totals.clients} icon={Building2} />
        <Metric label="Managed Assets" value={totals.assets} icon={Monitor} />
        <Metric label="Managed Users" value={totals.users} icon={Users} />
        <Metric label="Open Tickets" value={totals.tickets} icon={Ticket} />
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
        <form onSubmit={onSearch} className="flex flex-col xl:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search company, industry, contact, email…"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>

          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">All Stages</option>
            <option value="lead">Lead</option>
            <option value="prospect">Prospect</option>
            <option value="onboarding">Onboarding</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="offboarding">Offboarding</option>
          </select>

          <button className="bg-zinc-800 hover:bg-zinc-700 text-sm px-4 py-2 rounded-md">
            Search
          </button>
        </form>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Tier</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-left px-4 py-3">Assets</th>
              <th className="text-left px-4 py-3">Tickets</th>
              <th className="text-left px-4 py-3">Health</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan="8" className="px-4 py-10 text-center text-zinc-500">
                  Loading companies…
                </td>
              </tr>
            )}

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan="8" className="px-4 py-10 text-center text-zinc-500">
                  No companies found. Add the first client before building more MSP workflows.
                </td>
              </tr>
            )}

            {!loading &&
              items.map((c) => (
                <tr key={c.id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                  <td className="px-4 py-3">
                    <Link to={`/clients/${c.id}`} className="group">
                      <div className="font-medium text-zinc-100 flex items-center gap-2">
                        <Building2 size={14} className="text-blue-400" />
                        {c.name}
                        <ArrowUpRight size={12} className="text-zinc-700 group-hover:text-zinc-300" />
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {c.industry || "No industry"} · {c.country || "No country"}
                      </div>
                    </Link>
                  </td>

                  <td className="px-4 py-3">
                    <div>{c.primary_contact_name || "—"}</div>
                    <div className="text-xs text-zinc-500">{c.primary_contact_email || "No email"}</div>
                  </td>

                  <td className="px-4 py-3">
                    <Badge>{c.support_tier || c.tier || "Silver"}</Badge>
                  </td>

                  <td className="px-4 py-3">
                    <Badge>{c.lifecycle_stage || "active"}</Badge>
                  </td>

                  <td className="px-4 py-3 font-mono text-xs">
                    {c.asset_count || c.assets_count || 0}
                  </td>

                  <td className="px-4 py-3 font-mono text-xs">
                    {c.open_ticket_count || c.tickets_open || 0}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Ring value={c.health_score || 0} label="" size={42} />
                      <span className="font-mono text-xs text-zinc-400">{c.health_score || 0}%</span>
                    </div>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(c)}
                        className="p-2 rounded-md bg-zinc-900 hover:bg-zinc-800 text-zinc-300"
                        title="Edit company"
                      >
                        <Pencil size={14} />
                      </button>

                      <button
                        onClick={() => archiveClient(c)}
                        className="p-2 rounded-md bg-zinc-900 hover:bg-red-950 text-zinc-300 hover:text-red-300"
                        title="Archive company"
                      >
                        <Archive size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl">
            <form onSubmit={saveClient}>
              <div className="p-5 border-b border-zinc-900 flex items-center justify-between">
                <div>
                  <h2 className="font-display text-xl font-semibold">
                    {editing ? "Edit Company" : "Add Company"}
                  </h2>
                  <p className="text-sm text-zinc-500 mt-1">
                    Company profile, contacts, MSP ownership, lifecycle, and scoring.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="text-zinc-500 hover:text-zinc-200"
                >
                  Close
                </button>
              </div>

              <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <Field label="Company Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <Field label="Legal Name" value={form.legal_name} onChange={(v) => setForm({ ...form, legal_name: v })} />
                <Field label="Industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} />

                <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
                <Field label="Region" value={form.region} onChange={(v) => setForm({ ...form, region: v })} />
                <Field label="Timezone" value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })} />

                <Field label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} />
                <Field label="Employee Count" type="number" value={form.employee_count} onChange={(v) => setForm({ ...form, employee_count: v })} />

                <SelectField label="Support Tier" value={form.support_tier} onChange={(v) => setForm({ ...form, support_tier: v, tier: v })} options={["Bronze", "Silver", "Gold", "Platinum"]} />
                <SelectField label="Lifecycle Stage" value={form.lifecycle_stage} onChange={(v) => setForm({ ...form, lifecycle_stage: v })} options={["lead", "prospect", "onboarding", "active", "suspended", "offboarding"]} />
                <SelectField label="Contract Status" value={form.contract_status} onChange={(v) => setForm({ ...form, contract_status: v })} options={["active", "pending", "expired", "none"]} />

                <Field label="Primary Contact" value={form.primary_contact_name} onChange={(v) => setForm({ ...form, primary_contact_name: v })} />
                <Field label="Primary Email" value={form.primary_contact_email} onChange={(v) => setForm({ ...form, primary_contact_email: v })} />
                <Field label="Primary Phone" value={form.primary_contact_phone} onChange={(v) => setForm({ ...form, primary_contact_phone: v })} />

                <Field label="Assigned Engineer" value={form.assigned_engineer} onChange={(v) => setForm({ ...form, assigned_engineer: v })} />
                <Field label="Account Manager" value={form.assigned_account_manager} onChange={(v) => setForm({ ...form, assigned_account_manager: v })} />
                <Field label="vCIO" value={form.assigned_vcio} onChange={(v) => setForm({ ...form, assigned_vcio: v })} />

                <Field label="Health Score" type="number" value={form.health_score} onChange={(v) => setForm({ ...form, health_score: v })} />
                <Field label="Compliance Score" type="number" value={form.compliance_score} onChange={(v) => setForm({ ...form, compliance_score: v })} />
                <Field label="Security Score" type="number" value={form.security_score} onChange={(v) => setForm({ ...form, security_score: v })} />

                <div className="xl:col-span-3">
                  <label className="text-xs text-zinc-500">Notes</label>
                  <textarea
                    value={form.notes || ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={4}
                    className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="p-5 border-t border-zinc-900 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 rounded-md bg-zinc-900 hover:bg-zinc-800 text-sm"
                >
                  Cancel
                </button>

                <button
                  disabled={saving}
                  className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-sm text-white"
                >
                  {saving ? "Saving…" : editing ? "Save Changes" : "Create Company"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, icon: Icon }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">{label}</div>
        <Icon size={15} className="text-blue-400" />
      </div>
      <div className="font-display text-2xl font-semibold mt-2">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <label className="text-xs text-zinc-500">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs text-zinc-500">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-zinc-800 bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-300">
      <ShieldCheck size={10} className="text-blue-400" />
      {children}
    </span>
  );
}
